import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';

import icons from '../../utils/assets/icons';

import SearchInput from '../SearchInput';

import './index.scss';

function heightOfChildren(childrenArray, numberOfVisibleItems) {
  if (numberOfVisibleItems && childrenArray.length > numberOfVisibleItems) {
    return childrenArray[numberOfVisibleItems].offsetTop + 2;
  }

  const lastChild = childrenArray[childrenArray.length - 1];

  return lastChild.offsetTop + lastChild.offsetHeight + 2;
}

export function heightFromRef(ref, numberOfVisibleItems) {
  if (ref.current) {
    const childNodes = Array.from(ref.current.childNodes || []);
    return heightOfChildren(childNodes, numberOfVisibleItems);
  }
  return 0;
}

function CallBackOnReRender({ children, callback }) {
  useEffect(() => {
    callback();
  });
  return children;
}

export default function DropDown({
  children,
  foldOnSelect,
  numberOfVisibleItems,
  onSearch,
  placeholder,
  searchPlaceholder,
  selectedValue,
  withSearch,
}) {
  const searchInputRef = useRef(null);
  const itemsRef = useRef(null);
  const [height, setHeight] = useState(0);
  const [searchText, setSearchText] = useState('');

  const [isOpen, setIsOpen] = useState(false);

  const searchHeight = (searchInputRef.current && searchInputRef.current.offsetHeight) || 0;

  useEffect(() => {
    setHeight(heightFromRef(itemsRef, numberOfVisibleItems));
  }, [isOpen]);

  useEffect(() => {
    onSearch(searchText);
  }, [searchText]);

  useEffect(() => {
    setSearchText('');
  }, [isOpen]);

  useEffect(() => {
    function handleClick() {
      setIsOpen(false);
    }

    function handleEsc({ key }) {
      if (key === 'Escape') setIsOpen(false);
    }

    window.addEventListener('click', handleClick);
    window.addEventListener('keydown', handleEsc);

    return function cleanup() {
      window.removeEventListener('click', handleClick);
      window.removeEventListener('keydown', handleEsc);
    };
  }, []);

  function toggleDropdown(e) {
    e.stopPropagation();
    setIsOpen(open => !open);
  }

  const openClassName = isOpen ? 'DropDown--open' : '';

  return (
    <div className={`DropDown ${openClassName}`}>
      <div
        className="DropDown__title"
        onClick={toggleDropdown}
        title={selectedValue || placeholder}
      >
        <span className="DropDown__placeholder">{selectedValue || placeholder}</span>
        <img className="DropDown__chevron" src={icons.chevronDown} alt="down-chevron" />
      </div>

      {isOpen && (
        <div className="DropDown__itemsWrapper" style={{ height: isOpen ? `${height}px` : '0' }}>
          {withSearch && (
            <div
              className="DropDown__search"
              ref={searchInputRef}
              onClick={e => {
                e.stopPropagation();
              }}
            >
              <SearchInput
                value={searchText}
                placeholder={searchPlaceholder}
                onChange={({ target: { value } }) => {
                  setSearchText(value);
                }}
              />
            </div>
          )}

          <div
            ref={itemsRef}
            className="DropDown__items"
            style={{ height: isOpen ? `${height - searchHeight}px` : '0' }}
            onClick={e => {
              if (!foldOnSelect) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          >
            <CallBackOnReRender
              callback={() => {
                setHeight(heightFromRef(itemsRef, numberOfVisibleItems));
                if (searchInputRef.current) {
                  searchInputRef.current.querySelector('input').focus();
                }
              }}
            >
              {children}
            </CallBackOnReRender>
          </div>
        </div>
      )}
    </div>
  );
}

DropDown.propTypes = {
  children: PropTypes.node.isRequired,
  foldOnSelect: PropTypes.bool,
  numberOfVisibleItems: PropTypes.number,
  onSearch: PropTypes.func,
  placeholder: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  withSearch: PropTypes.bool,
};

DropDown.defaultProps = {
  foldOnSelect: true,
  numberOfVisibleItems: undefined,
  onSearch: () => {},
  placeholder: '-- Select --',
  searchPlaceholder: 'Search',
  selectedValue: null,
  withSearch: false,
};
